import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, Avatar, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, CircularProgress,
  InputAdornment, IconButton, Alert, Divider, Rating
} from '@mui/material';
import { Add, Search, Close, PersonAdd, Star, CalendarMonth, Phone, Email } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const avatarBgs = ['#f0fdfa','#eef2ff','#fdf4ff','#fff7ed','#fef2f2','#f0fdf4','#eff6ff'];
const avatarColors = ['#0d9488','#6366f1','#a855f7','#f97316','#ef4444','#22c55e','#3b82f6'];

const defaultForm = {
  name: '', email: '', phone: '', password: '',
  specialization: '', subSpecialization: '', department: '',
  registrationNumber: '', consultationFee: 0, followUpFee: 0, emergencyFee: 0,
  totalExperienceYears: 0, bio: '', languages: ''
};

const Doctors = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [qualifications, setQualifications] = useState([{ degree: '', institution: '', year: '' }]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [drRes, deptRes] = await Promise.all([
        api.get('/doctors', { params: { search, department: filterDept || undefined, limit: 50 } }),
        api.get('/departments')
      ]);
      setDoctors(drRes.data.data || []);
      setDepartments(deptRes.data.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search, filterDept]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.specialization) { setError('Name, email and specialization required'); return; }
    setError(''); setSubmitting(true);
    try {
      await api.post('/doctors', {
        ...form,
        qualifications: qualifications.filter(q => q.degree),
        languages: form.languages ? form.languages.split(',').map((s: string) => s.trim()) : []
      });
      toast.success('Doctor registered');
      setOpen(false);
      setForm(defaultForm);
      setQualifications([{ degree: '', institution: '', year: '' }]);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSubmitting(false); }
  };

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const setQual = (i: number, k: string, v: string) =>
    setQualifications(q => q.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Doctors</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14, mt: 0.3 }}>
            Available Doctors <Typography component="span" sx={{ fontWeight: 800, color: '#0d9488', fontSize: 16 }}>{doctors.filter(d => d.acceptingNewPatients).length}</Typography>
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setOpen(true); setError(''); setForm(defaultForm); }}
          sx={{ borderRadius: 2.5, background: 'linear-gradient(135deg, #0d9488, #0f766e)', textTransform: 'none', fontWeight: 600, px: 2.5, boxShadow: '0 4px 14px rgba(13,148,136,0.35)' }}>
          Register Doctor
        </Button>
      </Box>

      {/* Filters */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField placeholder="Search doctors..." value={search} onChange={e => setSearch(e.target.value)}
            size="small" sx={{ flex: 1, minWidth: 250, '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }} />
          <TextField select size="small" value={filterDept} onChange={e => setFilterDept(e.target.value)}
            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }} label="Department">
            <MenuItem value="">All Departments</MenuItem>
            {departments.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
          </TextField>
        </Box>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
      ) : (
        <Grid container spacing={2.5}>
          {doctors.map((doc, idx) => {
            const bg = avatarBgs[idx % avatarBgs.length];
            const color = avatarColors[idx % avatarColors.length];
            const quals = doc.qualifications?.map((q: any) => q.degree).join(', ') || '';
            return (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={doc._id}>
                <Card elevation={0} onClick={() => navigate(`/doctors/${doc._id}`)}
                  sx={{ border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', overflow: 'hidden',
                    '&:hover': { boxShadow: '0 12px 32px rgba(13,148,136,0.14)', transform: 'translateY(-3px)', borderColor: '#99f6e4' },
                    transition: 'all 0.25s' }}>

                  {/* Card Header */}
                  <Box sx={{ background: `linear-gradient(135deg, ${color}15, ${color}08)`, p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative' }}>
                    {/* Status dot */}
                    <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: doc.acceptingNewPatients ? '#10b981' : '#f59e0b', boxShadow: `0 0 0 3px ${doc.acceptingNewPatients ? '#d1fae5' : '#fef3c7'}` }} />
                    </Box>

                    <Avatar sx={{ width: 72, height: 72, bgcolor: bg, color, fontSize: 24, fontWeight: 800, mb: 1.5, border: `3px solid ${color}25`, boxShadow: `0 4px 14px ${color}30` }}>
                      {doc.user?.name?.split(' ').slice(-2).map((n: string) => n.charAt(0)).join('')}
                    </Avatar>
                    <Typography sx={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>Dr. {doc.user?.name}</Typography>
                    <Typography sx={{ fontSize: 12, color, fontWeight: 600, mt: 0.3 }}>{doc.specialization}</Typography>
                    {quals && (
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.3 }}>{quals}</Typography>
                    )}
                    {doc.department && (
                      <Chip label={doc.department.name} size="small" sx={{ mt: 1, bgcolor: `${color}12`, color, fontSize: 10, height: 20 }} />
                    )}
                  </Box>

                  <CardContent sx={{ p: 2 }}>
                    {/* Schedule */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 2 }}>
                      {doc.weeklySchedule?.filter((s: any) => s.isWorking).slice(0, 2).map((s: any, i: number) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{s.day?.slice(0,3)}-{['monday','tuesday','wednesday','thursday','friday'].includes(s.day) ? 'Fri' : 'Sun'}</Typography>
                          <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{s.startTime} - {s.endTime}</Typography>
                        </Box>
                      ))}
                      {!doc.weeklySchedule?.length && (
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Schedule not set</Typography>
                      )}
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Fees & Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>₹{doc.consultationFee || 0}</Typography>
                        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Consultation</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{doc.totalExperienceYears || 0}+</Typography>
                        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Years Exp.</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Star sx={{ fontSize: 14, color: '#f59e0b' }} />
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>4.8</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>Rating</Typography>
                      </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<CalendarMonth sx={{ fontSize: 14 }} />}
                        onClick={e => { e.stopPropagation(); navigate(`/appointments`); }}
                        sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: 12, borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#0d9488', color: '#0d9488', bgcolor: '#f0fdfa' } }}>
                        Schedule
                      </Button>
                      <Button size="small" variant="contained"
                        onClick={e => { e.stopPropagation(); navigate(`/appointments`); }}
                        sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: 12, background: 'linear-gradient(135deg, #0d9488, #0f766e)', boxShadow: 'none' }}>
                        Book Now
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {doctors.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 10, color: '#94a3b8' }}>
                <Typography sx={{ fontSize: 16 }}>No doctors registered yet</Typography>
                <Typography sx={{ fontSize: 13, mt: 0.5 }}>Click "Register Doctor" to add your first doctor</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Register Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>
          Register New Doctor
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0d9488', borderBottom: '2px solid #ccfbf1', pb: 0.5, mb: 1 }}>Personal Information</Typography>
            </Box>
            {[{ label: 'Full Name *', k: 'name' }, { label: 'Email *', k: 'email' }, { label: 'Phone', k: 'phone' }, { label: 'Password', k: 'password', type: 'password' }].map(({ label, k, type }) => (
              <Box key={k}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>{label}</Typography>
                <TextField fullWidth size="small" type={type || 'text'} value={form[k]} onChange={e => set(k, e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            ))}

            <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0d9488', borderBottom: '2px solid #ccfbf1', pb: 0.5, mb: 1 }}>Medical Details</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Specialization *</Typography>
              <TextField fullWidth size="small" value={form.specialization} onChange={e => set('specialization', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Department</Typography>
              <TextField select fullWidth size="small" value={form.department} onChange={e => set('department', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="">None</MenuItem>
                {departments.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Registration Number</Typography>
              <TextField fullWidth size="small" value={form.registrationNumber} onChange={e => set('registrationNumber', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Years of Experience</Typography>
              <TextField fullWidth size="small" type="number" value={form.totalExperienceYears} onChange={e => set('totalExperienceYears', Number(e.target.value))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>

            <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0d9488', borderBottom: '2px solid #ccfbf1', pb: 0.5, mb: 1 }}>Consultation Fees (₹)</Typography>
            </Box>
            {[{ label: 'Consultation', k: 'consultationFee' }, { label: 'Follow-up', k: 'followUpFee' }, { label: 'Emergency', k: 'emergencyFee' }, { label: 'Online', k: 'onlineConsultationFee' }].map(({ label, k }) => (
              <Box key={k}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>{label} (₹)</Typography>
                <TextField fullWidth size="small" type="number" value={form[k] || 0} onChange={e => set(k, Number(e.target.value))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            ))}

            <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0d9488', borderBottom: '2px solid #ccfbf1', pb: 0.5, mb: 1 }}>Qualifications</Typography>
              {qualifications.map((q, i) => (
                <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 1, mb: 1 }}>
                  <TextField size="small" placeholder="Degree (MBBS, MD...)" value={q.degree} onChange={e => setQual(i, 'degree', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <TextField size="small" placeholder="Institution" value={q.institution} onChange={e => setQual(i, 'institution', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <TextField size="small" placeholder="Year" type="number" value={q.year} onChange={e => setQual(i, 'year', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                  <IconButton size="small" onClick={() => setQualifications(q => q.filter((_, idx) => idx !== i))} disabled={qualifications.length === 1} sx={{ color: '#ef4444' }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" onClick={() => setQualifications(q => [...q, { degree: '', institution: '', year: '' }])}
                sx={{ textTransform: 'none', color: '#0d9488', fontSize: 12 }}>+ Add Qualification</Button>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Languages (comma separated)</Typography>
              <TextField fullWidth size="small" value={form.languages} onChange={e => set('languages', e.target.value)} placeholder="English, Hindi, Tamil..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Bio / About</Typography>
              <TextField fullWidth size="small" multiline rows={3} value={form.bio} onChange={e => set('bio', e.target.value)} placeholder="Brief professional summary..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #0d9488, #0f766e)', px: 3, boxShadow: '0 4px 14px rgba(13,148,136,0.35)' }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Register Doctor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Doctors;
